/**
 * 003 — expandable reason list of one suggestion (FR-012, FR-UI-001): the
 * signals that produced it, whether a rule proposed it, a rule conflict, and
 * per target document the proposed amount, the remaining open amount and an
 * "ungeprüft" hint for receipts nobody has reviewed yet.
 */
import { StatusBadge } from '../ui';
import { day, money, type Suggestion } from '../../lib/bank-api';

/** In-app detail page of an invoice (001, read-only) or expense; opened in a new tab. */
export const documentHref = (kind: 'invoice' | 'expense', id: string) =>
  `/${kind === 'invoice' ? 'invoices' : 'expenses'}/${encodeURIComponent(id)}`;

export function SuggestionReasons({ suggestion }: { suggestion: Suggestion }) {
  const rule = suggestion.origin === 'rule';
  return (
    <details className="text-sm" data-testid="suggestion-reasons">
      <summary className="cursor-pointer text-white/70">
        <span className="inline-flex flex-wrap items-center gap-2 align-middle">
          <span>Gründe ({suggestion.signals.length})</span>
          {rule && (
            <StatusBadge tone="info" data-testid="suggestion-origin-rule">
              Regel
            </StatusBadge>
          )}
          {suggestion.conflict && (
            <StatusBadge tone="warning" data-testid="suggestion-conflict">
              Konflikt
            </StatusBadge>
          )}
        </span>
      </summary>
      <div className="mt-2 space-y-2 border-l border-white/10 pl-3">
        <ul className="list-disc pl-4 text-white/80">
          {suggestion.signals.map((s, i) => (
            <li key={`${s.code}-${i}`}>{s.label}</li>
          ))}
        </ul>
        {rule && <p className="text-white/60">Vorgeschlagen durch eine Abgleichsregel.</p>}
        {suggestion.conflict && (
          <p className="text-amber-200">Widersprüchliche Regeln treffen zu — bitte die Zuordnung prüfen.</p>
        )}
        {suggestion.targets.length > 0 && (
          <ul className="space-y-1" aria-label="Vorgeschlagene Belege">
            {suggestion.targets.map((t) => (
              <li key={`${t.kind}:${t.documentId}`} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <a
                  href={documentHref(t.kind, t.documentId)}
                  target="_blank"
                  rel="noreferrer"
                  title="Beleg in neuem Tab öffnen"
                  className="min-w-0 break-words text-white/90 underline decoration-white/30 hover:decoration-white"
                >
                  {t.label}
                </a>
                {t.date && <span className="text-white/50">{day(t.date)}</span>}
                <span className="font-medium">{money(t.amountCents)}</span>
                <span className="text-white/50">offener Rest {money(t.remainingCents)}</span>
                {t.unreviewed && (
                  <StatusBadge tone="warning" title="Beleg noch nicht geprüft">
                    ungeprüft
                  </StatusBadge>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}
